import { HttpMethod } from '@teqplay/api'

import { requestBuilder } from '../builder'

export type ISODateTimeString = string

export type VoyageStatus = 'IN_DEPARTURE_PORT' | 'EN_ROUTE' | 'IN_ARRIVAL_PORT'

export type VesselCategory =
  | 'BULK_CARRIER'
  | 'CONTAINER'
  | 'FISHING'
  | 'GENERAL_CARGO'
  | 'OFFSHORE'
  | 'PASSENGER'
  | 'PLEASURE_CRAFT'
  | 'TANKER'
  | 'TUG'

export type SHARE_MEDIUM = 'WHATSAPP' | 'EMAIL'

export const SHARE_MEDIUM = {
  WHATSAPP: 'WHATSAPP',
  EMAIL: 'EMAIL'
} as const

export type PortRef = {
  id: string
  unlocode: string
  name: string
}

export type LegTime = {
  port?: PortRef
  estimatedTime?: ISODateTimeString
  actualTime?: ISODateTimeString
}

export type Country = {
  code?: string
  name?: string
}

export type GeoPoint = {
  lat: number
  lon: number
}

export type Ship = {
  imo: string
  name?: string
  speedKnots?: number
  heading: number
  category: VesselCategory
  location?: GeoPoint
  eta: ISODateTimeString
  lastUpdated?: ISODateTimeString
  lengthOverall: number
  width: number
  draught?: number
  country: Country
  grossTonnage?: number
  deadWeightTonnage?: number
  owner?: string
}

export type Stats = {
  subscriptionAmount: number
  sharingAmount: number
  notesAmount?: number
}

export type TVesselJourneyResponse = {
  status?: VoyageStatus
  arrival?: LegTime
  vesselVoyageVoyageId: string
  travelTime?: string
  departure?: LegTime
  stats?: Stats
  type: string
  ship: Ship
}

export type TGetVesselJourneyInput = { imo: string }
export type TGetVesselJourneyOutput = TVesselJourneyResponse

const get = requestBuilder
  .type<TGetVesselJourneyInput, TGetVesselJourneyOutput>()
  .request(({ input: { imo } }) => {
    const params = new URLSearchParams({ imo })
    return {
      path: `/v1/vesselJourney?${params.toString()}`,
      method: HttpMethod.GET
    }
  })

export type TPostVesselJourneyInput = string[]
export type TPostVesselJourneyOutput = TVesselJourneyResponse[]

const post = requestBuilder
  .type<TPostVesselJourneyInput, TPostVesselJourneyOutput>()
  .request(({ input }) => {
    return {
      path: `/v1/vesselJourney`,
      method: HttpMethod.POST,
      init: {
        body: JSON.stringify(input)
      }
    }
  })
export type TRecipient = { recipient: string; medium: SHARE_MEDIUM }
export type TShareVesselJourneyInput = {
  imo: string
  portId: string
  recipients: TRecipient[]
  note: string
}
export type TShareVesselJourneyOutput = {
  _id: string
  imo: string
  vesselName: string
  arrivalPortId: string
  arrivalPortName: string
  voyageId: string
  host: string
  guest: {
    recipient: string
    medium: SHARE_MEDIUM
  }
  sharedAt: ISODateTimeString
  accessed: {
    email: string
    at: ISODateTimeString
  }[]
  expiresAt: ISODateTimeString
  enabled: boolean
  token: string
}[]

/**
 * Share vessel journey
 */

const share = requestBuilder
  .type<TShareVesselJourneyInput, TShareVesselJourneyOutput>()
  .request(({ input }) => ({
    path: `/v1/vesselJourney/share`,
    method: HttpMethod.POST,
    init: {
      body: JSON.stringify(input)
    }
  }))

export type TGetShareInput = { code: string }
export type TGetShareOutput = {
  shipJourney: TVesselJourneyResponse
  sharing: {
    host: string
    sharedAt: ISODateTimeString
    note: string
  }
}

const getShare = requestBuilder
  .type<TGetShareInput, TGetShareOutput>()
  .request(({ input: { code } }) => ({
    path: `/v1/vesselJourney/share/${encodeURIComponent(code)}`,
    method: HttpMethod.GET
  }))

export type TGetBookmarksInput = void
export type TGetBookmarksOutput = TVesselJourneyResponse[]

/**
 * Get bookmarks
 */

const getBookmarks = requestBuilder.type<TGetBookmarksInput, TGetBookmarksOutput>().request(() => ({
  path: `/v1/vesselJourney/bookmarks`,
  method: HttpMethod.GET
}))

export type TAddBookmarksInput = {
  imos: string[]
  returnFinalSubscriptionList?: boolean
}
export type TAddBookmarksOutput = TVesselJourneyResponse[]

const addBookmarks = requestBuilder
  .type<TAddBookmarksInput, TAddBookmarksOutput>()
  .request(({ input: { imos, returnFinalSubscriptionList } }) => {
    const params = new URLSearchParams()
    if (returnFinalSubscriptionList !== undefined) {
      params.set('returnFinalSubscriptionList', returnFinalSubscriptionList.toString())
    }
    const query = params.toString()
    return {
      path: `/v1/vesselJourney/bookmarks${query ? `?${query}` : ''}`,
      method: HttpMethod.POST,
      init: {
        body: JSON.stringify(imos)
      }
    }
  })

export type TDeleteBookmarksInput = {
  imos: string[]
  returnFinalSubscriptionList?: boolean
}
export type TDeleteBookmarksOutput = TVesselJourneyResponse[]

const deleteBookmarks = requestBuilder
  .type<TDeleteBookmarksInput, TDeleteBookmarksOutput>()
  .request(({ input: { imos, returnFinalSubscriptionList } }) => {
    const params = new URLSearchParams()
    if (returnFinalSubscriptionList !== undefined) {
      params.set('returnFinalSubscriptionList', returnFinalSubscriptionList.toString())
    }
    const query = params.toString()
    return {
      path: `/v1/vesselJourney/bookmarks${query ? `?${query}` : ''}`,
      method: HttpMethod.DELETE,
      init: {
        body: JSON.stringify(imos)
      }
    }
  })

export type TGetVesselJourneyByPortInput = {
  portId?: string
  unlocode?: string
  minimumShipLengthInMeters?: number
  journeyStatus?: VoyageStatus
}
export type TGetVesselJourneyByPortOutput = TVesselJourneyResponse[]

/**
 * GET /v1/vesselJourney/port
 * Get Ship Journey by Port UNLOCODE or Port ID.
 */
const getByPort = requestBuilder
  .type<TGetVesselJourneyByPortInput, TGetVesselJourneyByPortOutput>()
  .request(({ input: { portId, unlocode, minimumShipLengthInMeters, journeyStatus } }) => {
    const params = new URLSearchParams()

    if (portId && portId.trim()) params.set('portId', portId)
    if (unlocode && unlocode.trim()) params.set('unlocode', unlocode)
    if (minimumShipLengthInMeters !== undefined)
      params.set('minimumShipLengthInMeters', minimumShipLengthInMeters.toString())
    if (journeyStatus && journeyStatus.trim()) params.set('journeyStatus', journeyStatus)
    return {
      path: `/v1/vesselJourney/port?${params.toString()}`,
      method: HttpMethod.GET
    }
  })

const vesselJourney = {
  get,
  post,
  share,
  getBookmarks,
  addBookmarks,
  deleteBookmarks,
  getShare,
  getByPort
}
export default vesselJourney
